import React, {useEffect, useState} from 'react';
import {headerLinks, Images} from '@/constants';
import {Icon, Button} from '@/ui-kits';
import {useClickOutside} from '@/hooks';
import {useNavigate, useLocation} from 'react-router-dom';
import {Logo} from '../logo';
import {screenSize} from '@/theme';
import {cn} from '@/utils/common_functions';
import { Routes } from '@/navigation/Routes';

interface HeaderProps {
  variant?: 'home' | 'auth';
  cta?: {label: string; link: string; variant: 'primary' | 'secondary'}[];
}

export const Header: React.FC<HeaderProps> = props => {
  const {variant = 'home', cta} = props;
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const location = useLocation();

  // Handle hash navigation after route change
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: string) => {
    e.preventDefault();
    const [path, hash] = link.split('#');
    
    if (location.pathname === path) {
      // Same page - just scroll
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Different page - navigate then scroll
      navigate(link);
    }
    
    setOpen(false);
  };

  useEffect(() => {
    if (location.hash && location.pathname === Routes.INDEX) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });

          // Clear hash after scrolling
          window.history.replaceState(null, '', location.pathname);
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    return () => {
      if (!location.hash && location.pathname === Routes.INDEX) {
        window.scrollTo(0, 0);
      }
    }
  }, []);


  return (
    <div className={cn(
      "flex px-2 sm:px-4 relative gap-2 sm:gap-4 md:gap-8 2xl:gap-16 w-full justify-between items-center overflow-hidden",
      variant === 'auth' ? 'py-8 sm:py-10 md:py-12 lg:py-16' : 'py-2'
    )}>
      {variant === 'auth' && (
        <img
          src={Images.bg_hero}
          alt="header background"
          className="absolute top-0 left-0 w-full h-full z-[-1]"
        />
      )}
      <Logo />
      <div
        className={cn(
          `hidden xl:flex flex-row items-center bg-white gap-8 justify-between px-4 md:px-8 py-4 rounded-lg max-w-[${screenSize['xl']}px]`,
          variant === 'home' ? `w-full max-w-[960px]` : 'w-auto',
        )}>
        <div
          className={cn(
            'flex flex-1 mr-auto justify-between max-w-[640px]',
            variant === 'auth' ? 'gap-12' : 'gap-[auto]',
          )}>
          {headerLinks.map(item => (
            <a
              onClick={(e) => handleNavClick(e, item.link)}
              key={item.label}
              href={item.link}
              className="text-base font-Body text-ui_blue">
              {item.label}
            </a>
          ))}
        </div>
        {variant === 'home' ? (
          <div className="flex gap-4 ml-auto">
            {cta?.map(item => (
              <Button
                key={item.label}
                title={item.label}
                variant={item.variant}
                onClick={() => navigate(item.link)}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className={`flex xl:hidden items-center gap-6 sm:gap-6 min-w-0`}>
        {cta?.slice(0, 1)?.map(item => (
          <Button
            key={item.label}
            title={item.label}
            variant={item.variant}
            onClick={() => navigate(item.link)}
            className="px-3 sm:px-4 text-xs sm:text-base min-w-[70px] sm:min-w-0"
            textClassName="!text-xs sm:!text-base"
          />
        ))}
        <button onClick={() => setOpen(true)} className="flex-shrink-0 mr-4 sm:mr-0">
          <Icon type="menu" className="size-4" />
        </button>
      </div>

      {/* side bar */}
      <div
        ref={ref}
        tabIndex={open ? 0 : -1}
        style={{
          transform: `translate(${open ? '0px' : '100%'}, 0px)`,
          boxShadow: '0px 2px 21px 0px rgba(0, 0, 0, 0.25)',
        }}
        className={`bg-white transition-transform flex xl:hidden flex-col fixed top-0 right-0 border-l-[1px] border-l-ui_input_border z-[100] p-4 h-full max-w-[85%] sm:max-w-[50%] w-full`}>
        <div className="mb-10 self-center">
          <img alt="logo" src={Images.logo_transparent} className="w-[50px]" />
        </div>
        {headerLinks.map(item => (
          <a
            onClick={(e) => handleNavClick(e, item.link)}
            key={item.label}
            href={item.link}
            className={`text-base font-FigtreeMeduim text-ui_blue p-4 hover:bg-ui_sky_blue rounded-lg`}>
            {item.label}
          </a>
        ))}
        <div className="mt-auto" />
        {cta?.slice(1)?.map(item => (
          <Button
            key={item.label}
            title={item.label}
            onClick={() => navigate(item.link)}
          />
        ))}
      </div>
    </div>
  );
};
